import React, { useCallback, useEffect, useState } from 'react';
import { policyAPI, showApiError } from '../../services/api';
import {
  LIST_FILTER_OPTIONS,
  POLICY_OPTIONS,
  POLICY_TYPE_LABELS,
  formatDateTime,
} from './policyRegistrationShared';
import './PolicyRegistration.css';

const PolicyListPage = ({ onOpenEditor }) => {
  const [listFilter, setListFilter] = useState('ALL');
  const [newPolicyType, setNewPolicyType] = useState('TERMS_OF_SERVICE');
  const [policyList, setPolicyList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [activatingId, setActivatingId] = useState(null);

  const loadPolicyList = useCallback(async (filter) => {
    setListLoading(true);
    try {
      const data = filter === 'ALL'
        ? await policyAPI.listAllPolicies()
        : await policyAPI.listPoliciesByType(filter);
      setPolicyList(Array.isArray(data) ? data : []);
    } catch (error) {
      showApiError(error, '정책 목록을 불러오지 못했습니다.');
      setPolicyList([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPolicyList(listFilter);
  }, [listFilter, loadPolicyList]);

  const handleActivate = async (id, event) => {
    event.stopPropagation();
    if (!window.confirm('이 버전을 활성 정책으로 사용할까요?\n같은 종류의 다른 활성 정책은 비활성화됩니다.')) return;

    setActivatingId(id);
    try {
      await policyAPI.activatePolicy(id);
      window.alert('정책이 활성화되었습니다.');
      await loadPolicyList(listFilter);
    } catch (error) {
      showApiError(error, '정책 활성화에 실패했습니다.');
    } finally {
      setActivatingId(null);
    }
  };

  const handleOpenRow = (item) => {
    onOpenEditor({
      sourceId: item.id,
      policyType: item.policyType,
    });
  };

  const handleCreateNew = () => {
    const policyType = listFilter === 'ALL' ? newPolicyType : listFilter;
    onOpenEditor({ policyType });
  };

  const listFilterLabel = LIST_FILTER_OPTIONS.find((item) => item.type === listFilter)?.label || '전체';
  const showTypeColumn = listFilter === 'ALL';

  return (
    <div className="policy-registration policy-registration-list-view registration-section">
      <div className="policy-registration-toolbar">
        <label htmlFor="policy-list-filter">목록</label>
        <select
          id="policy-list-filter"
          value={listFilter}
          onChange={(event) => setListFilter(event.target.value)}
          disabled={listLoading}
        >
          {LIST_FILTER_OPTIONS.map((option) => (
            <option key={option.type} value={option.type}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => loadPolicyList(listFilter)}
          disabled={listLoading}
        >
          새로고침
        </button>

        {listFilter === 'ALL' && (
          <>
            <label htmlFor="policy-new-type">작성 종류</label>
            <select
              id="policy-new-type"
              value={newPolicyType}
              onChange={(event) => setNewPolicyType(event.target.value)}
              disabled={listLoading}
            >
              {POLICY_OPTIONS.map((option) => (
                <option key={option.type} value={option.type}>
                  {option.label}
                </option>
              ))}
            </select>
          </>
        )}

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleCreateNew}
          disabled={listLoading}
        >
          새로 작성
        </button>
      </div>

      <div className="policy-registration-list-panel policy-registration-list-panel-full">
        <div className="policy-registration-list-header">
          <h3>정책 목록</h3>
          <span>{listFilterLabel}</span>
        </div>

        {listLoading ? (
          <div className="policy-registration-list-state">목록을 불러오는 중…</div>
        ) : policyList.length === 0 ? (
          <div className="policy-registration-list-state">등록된 정책이 없습니다.</div>
        ) : (
          <div className="policy-registration-table-wrap">
            <table className="policy-registration-table">
              <thead>
                <tr>
                  <th>ID</th>
                  {showTypeColumn && <th>종류</th>}
                  <th>제목</th>
                  <th>버전</th>
                  <th>시행일시</th>
                  <th>활성</th>
                  <th>생성일시</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {policyList.map((item) => (
                  <tr
                    key={item.id}
                    className={[
                      'policy-registration-table-row',
                      item.active ? 'active-row' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleOpenRow(item)}
                  >
                    <td>{item.id}</td>
                    {showTypeColumn && (
                      <td>{POLICY_TYPE_LABELS[item.policyType] || item.policyType}</td>
                    )}
                    <td>{item.title}</td>
                    <td>{item.version || '-'}</td>
                    <td>{formatDateTime(item.effectiveDate)}</td>
                    <td>
                      <span className={`policy-status-badge ${item.active ? 'active' : 'inactive'}`}>
                        {item.active ? 'true' : 'false'}
                      </span>
                    </td>
                    <td>{formatDateTime(item.createdAt)}</td>
                    <td>
                      {!item.active && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={(event) => handleActivate(item.id, event)}
                          disabled={activatingId === item.id}
                        >
                          {activatingId === item.id ? '처리 중…' : '활성화'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="policy-registration-hint">
        행을 클릭하면 해당 내용을 불러와 새 버전으로 작성할 수 있습니다.
      </p>
    </div>
  );
};

export default PolicyListPage;
